import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import {
  adminGetRecordatorios,
  adminMarcarRecordatorioEnviado,
  adminMarcarTodosRecordatoriosEnviados,
} from '../../lib/portalClienteApi'

// Día por defecto: mañana (formato YYYY-MM-DD en local TZ)
function fechaManana() {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

function formatFecha(f) {
  if (!f) return '—'
  return new Date(f + 'T12:00:00').toLocaleDateString('es-UY', { weekday: 'long', day: 'numeric', month: 'long' })
}

function formatHora(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const VENTANA_LABEL = {
  manana: 'Mañana 10–14hs',
  tarde:  'Tarde 14–18hs',
}

function buildMensajeWA({ cliente_nombre, fecha_programada, ventana_horaria, direccion, apto, kit_codigo }) {
  const dia = formatFecha(fecha_programada)
  const ventana = VENTANA_LABEL[ventana_horaria] || ventana_horaria || ''
  const dirCompleta = apto ? `${direccion}, ${apto}` : direccion
  const nombre = (cliente_nombre || '').split(' ')[0]
  const conQue = kit_codigo
    ? ` con tu *${kit_codigo.replace(/_/g, ' ')}*`
    : ` con *los productos que seleccionaste*`
  return `Hola ${nombre}!\n\nTe escribimos de *Limpack* para recordarte que mañana *${dia}* pasamos por tu casa${conQue}.\n\nDirección: ${dirCompleta}\nFranja Horaria: ${ventana}\n\nSi tenés alguna duda o necesitás cambiar el horario, respondé este mensaje. ¡Gracias!`
}

function normalizarTelefono(tel) {
  if (!tel) return ''
  // Sacar todo lo que no sea dígito
  let n = (tel + '').replace(/\D/g, '')
  // Si arranca con 0, sacar el 0
  if (n.startsWith('0')) n = n.slice(1)
  // Si no empieza con 598 (UY), agregárselo
  if (!n.startsWith('598')) n = '598' + n
  return n
}

export default function Recordatorios() {
  const [fecha, setFecha] = useState(fechaManana())
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('todos') // todos | pendientes | enviados
  const [filtroBarrio, setFiltroBarrio] = useState('')

  useEffect(() => { load() }, [fecha])

  async function load() {
    setLoading(true)
    setItems(await adminGetRecordatorios(fecha))
    setLoading(false)
  }

  async function abrirWA(item) {
    const tel = normalizarTelefono(item.cliente_telefono)
    if (!tel || tel.length < 10) {
      alert('Este cliente no tiene WhatsApp registrado.')
      return
    }
    const msg = buildMensajeWA(item)
    const url = `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`
    window.open(url, '_blank')
    // marca optimista
    if (!item.recordatorio_enviado_at) {
      try {
        await adminMarcarRecordatorioEnviado(item.fuente, item.id)
        setItems(prev => prev.map(x =>
          x.id === item.id && x.fuente === item.fuente
            ? { ...x, recordatorio_enviado_at: new Date().toISOString() }
            : x
        ))
      } catch (e) {
        console.error('Marcando recordatorio:', e)
      }
    }
  }

  async function marcarManual(item) {
    try {
      await adminMarcarRecordatorioEnviado(item.fuente, item.id)
      setItems(prev => prev.map(x =>
        x.id === item.id && x.fuente === item.fuente
          ? { ...x, recordatorio_enviado_at: new Date().toISOString() }
          : x
      ))
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function marcarTodos() {
    if (!confirm('¿Marcar todos los pendientes como enviados?')) return
    try {
      await adminMarcarTodosRecordatoriosEnviados(fecha)
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  const barrios = useMemo(
    () => [...new Set(items.map(i => i.barrio).filter(Boolean))].sort(),
    [items]
  )

  const visibles = useMemo(() => {
    let f = items
    if (filtro === 'pendientes') f = f.filter(i => !i.recordatorio_enviado_at)
    if (filtro === 'enviados')   f = f.filter(i =>  i.recordatorio_enviado_at)
    if (filtroBarrio)            f = f.filter(i => i.barrio === filtroBarrio)
    return f
  }, [items, filtro, filtroBarrio])

  const stats = useMemo(() => {
    const total = items.length
    const enviados = items.filter(i => i.recordatorio_enviado_at).length
    return { total, enviados, pendientes: total - enviados }
  }, [items])

  return (
    <AdminLayout>
      <div className="page">
        <h1>Recordatorios WhatsApp</h1>
        <p className="lead">Avisá a los clientes que mañana pasás · 1 click abre WhatsApp con el mensaje listo</p>
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Entregas del día</h3>
            <div className="stat">{stats.total}</div>
          </div>
          <div className="card">
            <h3>Pendientes de avisar</h3>
            <div className="stat" style={{ color: stats.pendientes > 0 ? '#B45309' : 'var(--teal)' }}>
              {stats.pendientes}
            </div>
          </div>
          <div className="card">
            <h3>Avisados</h3>
            <div className="stat" style={{ color: 'var(--teal)' }}>{stats.enviados}</div>
          </div>
        </div>

        <div className="btn-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-ink, #1A3461)', letterSpacing: '.06em', textTransform: 'uppercase' }}>
            Fecha de entrega
          </label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--line, #E2E8F0)', fontFamily: 'inherit', fontSize: 14 }} />
          <button className="btn ghost" onClick={() => setFecha(fechaManana())}>Mañana</button>
          <button className="btn ghost" onClick={load}>↺</button>
          <div style={{ flex: 1 }} />
          {stats.pendientes > 0 && (
            <button className="btn ghost" onClick={marcarTodos} style={{ fontSize: 12 }}>
              ✓ Marcar todos como enviados
            </button>
          )}
        </div>

        <div className="btn-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 999, padding: 4 }}>
            {[
              { id: 'todos', label: `Todos (${stats.total})` },
              { id: 'pendientes', label: `Pendientes (${stats.pendientes})` },
              { id: 'enviados', label: `Enviados (${stats.enviados})` },
            ].map(f => (
              <button key={f.id}
                onClick={() => setFiltro(f.id)}
                style={{
                  padding: '6px 14px', fontSize: 12, borderRadius: 999, cursor: 'pointer',
                  background: filtro === f.id ? '#fff' : 'transparent',
                  border: filtro === f.id ? '1px solid var(--line, #E2E8F0)' : '1px solid transparent',
                  fontWeight: filtro === f.id ? 700 : 500,
                  color: 'var(--navy-ink, #1A3461)', fontFamily: 'inherit',
                }}>
                {f.label}
              </button>
            ))}
          </div>
          {barrios.length > 1 && (
            <select value={filtroBarrio} onChange={e => setFiltroBarrio(e.target.value)}
              style={{ padding: '6px 12px', fontSize: 12, borderRadius: 8, border: '1.5px solid var(--line, #E2E8F0)', fontFamily: 'inherit' }}>
              <option value="">Todos los barrios</option>
              {barrios.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
        </div>

        <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 12, marginBottom: 12 }}>
          Reparto del <strong>{formatFecha(fecha)}</strong>
        </p>

        {loading ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>Cargando...</div>
        ) : visibles.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>
            {items.length === 0
              ? `No hay entregas programadas para el ${formatFecha(fecha)}.`
              : 'No hay items con esos filtros.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibles.map(item => {
              const tel = normalizarTelefono(item.cliente_telefono)
              const sinTel = !tel || tel.length < 10
              const enviado = !!item.recordatorio_enviado_at
              return (
                <div key={`${item.fuente}-${item.id}`}
                  style={{
                    background: '#fff',
                    border: enviado ? '1.5px solid #86EFAC' : '1.5px solid var(--line, #E2E8F0)',
                    borderLeft: enviado ? '4px solid #16A34A' : '4px solid #F59E0B',
                    borderRadius: 12, padding: 14,
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 12, alignItems: 'center',
                  }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <strong style={{ fontSize: 15, color: 'var(--navy-ink, #1A3461)' }}>{item.cliente_nombre}</strong>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#F1F5F9', color: 'var(--navy-ink, #1A3461)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
                        {item.fuente === 'pedido' ? '📦 One-shot' : '⭐ Suscripción'}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{item.numero_orden}</span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--navy-ink, #1A3461)' }}>
                      📍 {item.direccion}{item.apto ? `, ${item.apto}` : ''} <span style={{ color: 'var(--muted)' }}>· {item.barrio}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                      {VENTANA_LABEL[item.ventana_horaria] || item.ventana_horaria}
                      {item.kit_codigo && <> · 📦 <span style={{ textTransform: 'capitalize' }}>{item.kit_codigo.replace(/_/g, ' ')}</span></>}
                      {sinTel && <span style={{ color: '#DC2626', marginLeft: 8 }}>· ⚠️ Sin WhatsApp</span>}
                    </div>
                    {enviado && (
                      <div style={{ fontSize: 11, color: '#16A34A', marginTop: 4, fontWeight: 700 }}>
                        ✓ Avisado el {formatHora(item.recordatorio_enviado_at)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'stretch' }}>
                    <button
                      onClick={() => abrirWA(item)}
                      disabled={sinTel}
                      style={{
                        padding: '10px 16px', borderRadius: 8, border: 0, cursor: sinTel ? 'not-allowed' : 'pointer',
                        background: enviado ? '#F1F5F9' : '#25D366', color: enviado ? 'var(--navy-ink, #1A3461)' : '#fff',
                        fontWeight: 800, fontSize: 13, fontFamily: 'inherit',
                        opacity: sinTel ? 0.4 : 1, whiteSpace: 'nowrap',
                      }}>
                      📲 {enviado ? 'Reenviar' : 'Mandar WA'}
                    </button>
                    {!enviado && !sinTel && (
                      <button onClick={() => marcarManual(item)}
                        style={{
                          padding: '4px 10px', fontSize: 11, borderRadius: 8,
                          background: 'transparent', border: 0, color: 'var(--muted)',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        Marcar enviado sin WA
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div style={{ marginTop: 24, padding: 12, background: '#F0FCFD', border: '1px solid #B3EEF3', borderRadius: 10, fontSize: 12, color: 'var(--navy-ink, #1A3461)' }}>
          💡 <strong>Tip:</strong> El botón abre WhatsApp Web/Mobile con el mensaje pre-cargado y marca el aviso como enviado. Si te equivocás, tocá <em>Reenviar</em> para abrirlo de vuelta.
        </div>
      </div>
    </AdminLayout>
  )
}
